import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { CommonIP, IP, IpTypeLabel, RelatedFileGroup, SubTypeByIpType, TrifoldDetailMap } from '@model/ftf-api.model';
import { IpIconComponent } from '@shared/components/ip-icon/ip-icon.component';
import { PaginatedAutocompleteComponent } from '@shared/components/paginated-autocomplete/paginated-autocomplete.component';
import { Observable } from 'rxjs';

import { UploadTableComponent } from './upload-table/upload-table.container.component';
import { TableEvent, TrifoldItemEvent, UploadTableFormControl } from './upload-table/upload-table.model';

@Component({
  selector: 'app-uploader-presentation',
  imports: [
    CommonModule,
    MatToolbar,
    MatToolbarRow,
    FormsModule,
    ReactiveFormsModule,
    IpIconComponent,
    MatButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatInput,
    MatAutocompleteTrigger,
    PaginatedAutocompleteComponent,
    MatCheckbox,
    MatProgressBar,
    UploadTableComponent,
  ],
  templateUrl: './uploader-presentation.component.html',
  styleUrl: './uploader-presentation.component.scss',
})
export class UploaderPresentationComponent {
  @ViewChild('autocomplete') autocomplete!: PaginatedAutocompleteComponent;
  @ViewChild('uploader') uploader!: ElementRef<HTMLInputElement>;
  @ViewChildren(UploadTableComponent) uploadTables!: QueryList<UploadTableComponent>;

  @Input({ required: true }) knownIpItems!: { identifier: string; ipType: IP.TypeEnum }[];
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) relFileGroups!: RelatedFileGroup[];
  @Input({ required: true }) uploadTableForms!: FormArray<FormGroup<UploadTableFormControl>>;
  @Input({ required: true }) searchItems$!: Observable<CommonIP[]>;
  @Input({ required: true }) paginationInfo$!: Observable<{ currentPage: number; totalPages: number }>;
  @Input({ required: true }) dependantDataLoaded!: boolean;
  @Input({ required: true }) uploadInitiated!: boolean;
  @Input({ required: true }) uploadedFilesProgress!: string;
  @Input({ required: true }) skippedFilesProgress!: string;
  @Input({ required: true }) allSubTypes!: SubTypeByIpType;
  @Input({ required: true }) trifoldMap!: TrifoldDetailMap;
  @Input({ required: true }) ipTypes!: IpTypeLabel<IP.TypeEnum>[];
  @Input({ required: true }) placeholderText!: string;
  @Input({ required: true }) hasUploadPermission!: (ipType: IP.TypeEnum) => boolean;
  @Input({ required: true }) progressPercentage!: number;

  @Output() pageChange = new EventEmitter<number>();
  @Output() browseFiles = new EventEmitter<Event>();
  @Output() uploadFiles = new EventEmitter<void>();
  @Output() addToClick = new EventEmitter<IP.TypeEnum>();
  @Output() searchSelectionChange = new EventEmitter<MatAutocompleteSelectedEvent>();
  @Output() checkboxChange = new EventEmitter<{ event: MatCheckboxChange; item: CommonIP }>();
  @Output() closeTable = new EventEmitter<TableEvent>();
  @Output() openFTF = new EventEmitter<TrifoldItemEvent>();
  @Output() openIPMS = new EventEmitter<TrifoldItemEvent>();

  /**
   * Gets the display string for a search result item.
   * @param value The search result item.
   * @returns The display string for the item.
   */
  searchDisplayFn(value: CommonIP | string): string {
    return (typeof value === 'string' ? value : value?.identifier) ?? '';
  }

  /**
   * Checks if the given item is selected.
   * @param item The CommonIP item to check.
   * @returns True if the item is selected, false otherwise.
   */
  isItemSelected(item: CommonIP): boolean {
    let ipType = item.ipType as IP.TypeEnum;
    if (ipType === 'Patent' && this.form.get('ipType')?.value === 'CaseReference') {
      ipType = 'CaseReference';
    }

    return this.knownIpItems.some((selected) => selected.identifier === item.identifier && selected.ipType === ipType);
  }

  /**
   * Gets the related file group by table ID.
   * @param tableId The ID of the table.
   * @returns The related file group, or an empty group if not found.
   */
  getRelatedFileGroupByTableId(tableId: string): RelatedFileGroup {
    const parts = tableId.split('||');
    const group = this.relFileGroups.find((group) => group.id === parts[2]);
    return group ?? { id: 'errored', display: '(unknown)', files: [] };
  }

  /**
   * Convert knownIpItems to CommonIP objects for the paginated autocomplete
   * This adds the missing properties required by CommonIP type
   */
  get selectedItemsForAutocomplete(): CommonIP[] {
    return this.knownIpItems.map(
      (item) =>
        ({
          identifier: item.identifier,
          ipType: item.ipType,
          title: '', // Add empty string for required title property
          family: '', // Add empty string for required family property
        }) as CommonIP
    );
  }

  onAddToClick(ipType: IP.TypeEnum): void {
    this.addToClick.emit(ipType);
  }

  onSearchSelectionChange($event: MatAutocompleteSelectedEvent): void {
    this.searchSelectionChange.emit($event);
  }

  onCheckboxChange(event: MatCheckboxChange, item: CommonIP): void {
    this.checkboxChange.emit({ event, item });
  }

  onCloseTable(event: TableEvent): void {
    this.closeTable.emit(event);
  }

  onPageChange(pageIndex: number): void {
    this.pageChange.emit(pageIndex);
  }

  onBrowseFiles(event: Event): void {
    this.browseFiles.emit(event);
  }

  onUploadFiles(): void {
    this.uploadFiles.emit();
  }

  onOpenInFTF(event: TrifoldItemEvent) {
    this.openFTF.emit(event);
  }

  onOpenInIPMS(event: TrifoldItemEvent) {
    this.openIPMS.emit(event);
  }
}
